import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, Share2 } from 'lucide-react';
import { getBlogPost, getAllBlogPosts } from '../../data/blogPosts';
import WhyIDontReferStrangers from './posts/WhyIDontReferStrangers';
import StopSayingJustHi from './posts/StopSayingJustHi';
import { useState } from 'react';

const postComponents: Record<string, React.ComponentType> = {
  'why-i-dont-refer-strangers': WhyIDontReferStrangers,
  'stop-saying-just-hi': StopSayingJustHi,
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [copied, setCopied] = useState(false);

  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  const post = getBlogPost(slug);
  const PostComponent = postComponents[slug];

  if (!post || !PostComponent) {
    return (
      <div className="container py-16">
        <div className="card text-center">
          <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-50">Post Not Found</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-400">
            Post not found. It may have been removed or doesn&apos;t exist.
          </p>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const allPosts = getAllBlogPosts();
  const currentIndex = allPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="container py-8">
          <Link
            to="/blog"
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>

          <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-gray-50">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Calendar size={16} />
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={16} />
              {post.readingTime}
            </span>
            <span>By {post.author}</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <span
                key={tag}
                className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 dark:bg-primary-950 dark:text-primary-300"
              >
                <Tag size={12} />
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-12">
        <div className="mx-auto max-w-3xl">
          <div className="card">
            <PostComponent />
          </div>

          {/* Share Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:shadow-md dark:bg-gray-800 dark:text-gray-300"
            >
              <Share2 size={16} />
              {copied ? 'Link Copied!' : 'Share Post'}
            </button>
          </div>

          {/* Navigation */}
          <div className="mt-12 grid gap-6 border-t border-gray-200 pt-8 md:grid-cols-2 dark:border-gray-800">
            {prevPost && (
              <Link
                to={`/blog/${prevPost.slug}`}
                className="card group transition-all hover:shadow-md"
              >
                <p className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">← Previous Post</p>
                <h3 className="font-semibold text-gray-900 transition-colors group-hover:text-primary-600 dark:text-gray-50 dark:group-hover:text-primary-400">
                  {prevPost.title}
                </h3>
              </Link>
            )}

            {nextPost && (
              <Link
                to={`/blog/${nextPost.slug}`}
                className="card group text-right transition-all hover:shadow-md md:col-start-2"
              >
                <p className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Next Post →</p>
                <h3 className="font-semibold text-gray-900 transition-colors group-hover:text-primary-600 dark:text-gray-50 dark:group-hover:text-primary-400">
                  {nextPost.title}
                </h3>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
